import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META } from "../lib/api";
import { useSession } from "../lib/session";
import {
  Rss, Plus, Trash2, Loader2, ExternalLink, RefreshCw, Newspaper, Play,
  Power, Pencil, AlertTriangle, Sparkles, Send, RotateCcw,
  ImageIcon, Clock, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { GenerationControls } from "./GenerationControls";

const inputCls =
  "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";
const labelCls = "text-xs font-semibold text-slate-400 mb-1.5 block";
const hintCls = "text-[11px] text-slate-600 mt-1";

const blankFeed = {
  url: "", name: "", content_type: "", active: false, check_interval: 120, category_filter: "",
  make_social: true, generate_mode: "auto", platforms: ["linkedin"], account_ids: [],
  brand_id: null, social_fmt: "viral", social_ratio: "1:1", image_model: "",
  auto_submit: false, approval_mode: "", quality_threshold: 7,
  link_to_article: true, link_ratio: 50, link_cta: "Read the full story",
  instagram_link_line: "Full story at the link in bio.", link_utm: true, link_track_clicks: false,
  make_article: false, wp_site_id: null, wp_category: null, wp_author: null,
  wp_status: "draft", article_words: 1200,
  inline_images_source: "ai_generated", inline_images_count: 2, auto_detect_image_count: false, social_embeds: true,
  featured_image_source: "ai_generated", featured_ratio: "16:9", featured_model: "",
};

// The featured image's options, as in the Enpulsed feed-to-publish workflow.
// How often a feed is checked for new articles.
const INTERVALS = [[15, "Every 15 minutes"], [30, "Every 30 minutes"], [60, "Every hour"], [120, "Every 2 hours"],
  [240, "Every 4 hours"], [360, "Every 6 hours"], [720, "Every 12 hours"], [1440, "Once a day"]];
const every = (m) => (INTERVALS.find(([v]) => v === m)?.[1] || `Every ${m >= 60 && m % 60 === 0 ? `${m / 60}h` : `${m} min`}`);
const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.round(mins / 60)}h ago` : `${Math.round(mins / 1440)}d ago`;
};
const nextIn = (f) => {
  if (!f.active) return "";
  if (!f.last_run) return "next check within a minute";
  const due = new Date(f.last_run).getTime() + (f.check_interval || 120) * 60000 - Date.now();
  const mins = Math.max(0, Math.round(due / 60000));
  return mins < 1 ? "next check within a minute" : `next in ${mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`}`;
};

export const FEATURED_SOURCES = [
  ["ai_generated", "AI generated"], ["feed_source", "From the feed"],
  ["pexels", "Pexels stock photo"], ["none", "No featured image"],
];

// One article, and the post it produced. The post is the point, so it gets the
// space: the image as it will publish, the caption as written, the hashtags.
// The headline stays as provenance underneath.
const FeedItemCard = ({ item, busy, onGenerate, onQueue, onReset }) => {
  const post = item.post;
  const [open, setOpen] = useState(false);
  const status = item.status || (item.used ? "ready" : "pending");
  const caption = post?.caption || "";
  const long = caption.length > 220;

  const badge = {
    pending: ["Not generated", "text-slate-500 bg-slate-800/60"],
    generating: ["Generating…", "text-indigo-300 bg-indigo-500/10"],
    ready: ["Ready", "text-emerald-400 bg-emerald-500/10"],
    queued: ["In queue", "text-amber-300 bg-amber-500/10"],
  }[status] || ["Not generated", "text-slate-500 bg-slate-800/60"];

  return (
    <div className="glass rounded-xl overflow-hidden" data-testid="feed-item">
      {post && (
        <div className="relative bg-slate-950/60 aspect-square max-h-72 flex items-center justify-center">
          {post.image_url ? (
            <img src={post.image_url} alt={post.topic || item.title}
              className="w-full h-full object-cover" loading="lazy" />
          ) : post.image_status === "pending" ? (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-[11px]">Rendering the image…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-600 px-6 text-center">
              <ImageIcon className="w-5 h-5" />
              <span className="text-[11px]">{post.image_error || "No image"}</span>
            </div>
          )}
          <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider
            px-2 py-0.5 rounded-full bg-slate-950/80 text-slate-300">{post.fmt}</span>
        </div>
      )}

      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge[1]}`}>{badge[0]}</span>
          <a href={item.url} target="_blank" rel="noreferrer"
            className="text-slate-600 hover:text-slate-300 shrink-0" title="Open the article">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {post ? (
          <>
            <p className="text-sm font-semibold text-slate-100 leading-snug">{post.topic}</p>
            {caption && (
              <p className="text-[12px] text-slate-400 leading-relaxed whitespace-pre-wrap">
                {open || !long ? caption : `${caption.slice(0, 220).trimEnd()}…`}
                {long && (
                  <button onClick={() => setOpen(!open)}
                    className="ml-1 text-indigo-400 hover:text-indigo-300 font-semibold">
                    {open ? "less" : "more"}
                  </button>
                )}
              </p>
            )}
            {!!post.hashtags?.length && (
              <p className="text-[11px] text-indigo-400/80 break-words">
                {post.hashtags.map((h) => `#${h}`).join(" ")}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-300 leading-snug">{item.title}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-600 pt-0.5">
          <span>{item.source_name}</span>
          {post && <span className="text-slate-700">·</span>}
          {post && <span className="truncate max-w-[10rem]">from “{item.title}”</span>}
        </div>

        {status === "skipped" && item.skip_reason && (
          <p className="text-[11px] text-amber-300 flex items-start gap-1.5">
            <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{item.skip_reason}</span>
          </p>
        )}
        {item.error && (
          <p className="text-[11px] text-rose-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{item.error}</span>
          </p>
        )}

        <div className="flex items-center gap-2 pt-1">
          {status === "queued" ? (
            <span className="flex items-center gap-1.5 text-[11px] text-amber-300 font-semibold">
              <Clock className="w-3.5 h-3.5" /> Waiting for approval
            </span>
          ) : post ? (
            <>
              <button onClick={onQueue} disabled={!!busy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600
                  disabled:opacity-60 text-white text-[11px] font-semibold">
                {busy === "queueing"
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5" />}
                Send to queue
              </button>
              <button onClick={onReset} disabled={!!busy} title="Discard and start again"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-800
                  hover:border-slate-700 text-slate-400 text-[11px] font-semibold disabled:opacity-60">
                <RotateCcw className="w-3.5 h-3.5" /> Redo
              </button>
            </>
          ) : (
            <>
              <button onClick={onGenerate} disabled={!!busy || status === "generating"}
                data-testid="generate-item"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600
                  disabled:opacity-60 text-white text-[11px] font-semibold">
                {busy === "generating" || status === "generating"
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
                  : <><Sparkles className="w-3.5 h-3.5" /> Generate post</>}
              </button>
              {(status === "generating" || item.error) && (
                // A generation interrupted by a restart would otherwise stay
                // marked in-progress with nothing to clear it.
                <button onClick={onReset} disabled={!!busy} title="Clear and try again"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-800
                    hover:border-slate-700 text-slate-400 text-[11px] font-semibold disabled:opacity-60">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const NewsView = () => {
  const { me } = useSession();
  const [sources, setSources] = useState([]);
  const [items, setItems] = useState([]);
  const [itemFilter, setItemFilter] = useState("all");
  const [brands, setBrands] = useState([]);
  const [sites, setSites] = useState([]);
  const [models, setModels] = useState([]);
  const [ratios, setRatios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState({ categories: [], authors: [] });
  const [accounts, setAccounts] = useState([]);

  const load = async () => {
    try {
      const [s, f, b, w, m, acc] = await Promise.all([
        api.get("/sources"), api.get("/feed"), api.get("/brands"),
        api.get("/wp-sites"), api.get("/image-models"), api.get("/accounts"),
      ]);
      setSources(s.data); setItems(f.data); setBrands(b.data); setSites(w.data);
      setModels(m.data.models || []); setRatios(m.data.ratios || []);
      setAccounts(acc.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Feeds generate in the background; keep the counters moving.
  useEffect(() => {
    const t = setInterval(() => api.get("/sources").then(({ data }) => setSources(data)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, []);

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Platforms and accounts have to stay consistent: an account selected on a
  // platform that is switched off would publish nowhere, and a platform left on
  // with all its accounts unticked means "every account", which is the default
  // people expect from a feed.
  const toggleWholePlatform = (key, mine) => setForm((c) => {
    const on = (c.platforms || []).includes(key);
    return {
      ...c,
      platforms: on ? (c.platforms || []).filter((x) => x !== key) : [...(c.platforms || []), key],
      account_ids: on
        ? (c.account_ids || []).filter((id) => !mine.some((a) => a.id === id))
        : (c.account_ids || []),
    };
  });

  const toggleAccount = (key, id) => setForm((c) => {
    const ids = c.account_ids || [];
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    return {
      ...c,
      account_ids: next,
      platforms: (c.platforms || []).includes(key) ? c.platforms : [...(c.platforms || []), key],
    };
  });

  const loadTaxonomy = async (siteId) => {
    if (!siteId) return setTaxonomy({ categories: [], authors: [] });
    try {
      const { data } = await api.get(`/wp-sites/${siteId}/taxonomy`);
      setTaxonomy(data);
    } catch { setTaxonomy({ categories: [], authors: [] }); }
  };

  const openForm = (feed) => {
    // A new feed starts with the platforms that have an account, not a
    // LinkedIn default that a workspace without LinkedIn cannot even see.
    const connected = [...new Set(accounts.map((a) => a.platform))];
    setForm(feed ? { ...blankFeed, ...feed }
      : { ...blankFeed, platforms: connected.length ? connected : blankFeed.platforms });
    loadTaxonomy(feed?.wp_site_id);
  };

  const save = async () => {
    if (!form.url.trim()) return toast.error("Feed URL is required");
    setSaving(true);
    try {
      if (form.id) await api.put(`/sources/${form.id}`, form);
      else await api.post("/sources", form);
      toast.success(form.id ? "Feed updated" : "Feed added");
      setForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that feed", { duration: 12000 });
    } finally { setSaving(false); }
  };

  const toggleActive = async (feed) => {
    await api.put(`/sources/${feed.id}`, { ...feed, active: !feed.active });
    toast(feed.active ? `${feed.name} paused` : `${feed.name} is now active`);
    load();
  };

  const runNow = async (feed) => {
    try {
      await api.post(`/sources/${feed.id}/run`);
      toast.success(`Processing ${feed.name} — new articles will appear in Drafts`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start that run"); }
  };

  const remove = async (id) => { await api.delete(`/sources/${id}`); toast("Feed removed"); load(); };

  // Dismissed articles are gone for good; failed ones are counted apart so
  // they stop crowding the ones that worked.
  const visible = items.filter((it) => it.status !== "dismissed");
  const kind = (it) => {
    const st = it.status || (it.used ? "ready" : "pending");
    if (["ready", "queued"].includes(st)) return "ready";
    if (st === "skipped") return "skipped";
    if (it.error && st !== "generating") return "failed";
    return "todo";
  };
  const itemCounts = visible.reduce((acc, it) => ({ ...acc, [kind(it)]: acc[kind(it)] + 1 }),
    { all: visible.length, ready: 0, todo: 0, failed: 0, skipped: 0 });
  const shownItems = itemFilter === "all" ? visible : visible.filter((it) => kind(it) === itemFilter);

  const dismissFailed = async () => {
    try {
      const { data } = await api.post("/feed-items/dismiss-failed", {});
      toast.success(`Dismissed ${data.dismissed} failed article${data.dismissed === 1 ? "" : "s"} - they won't be retried`);
      await load();
    } catch { toast.error("Could not dismiss them"); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/feed?refresh=true");
      setItems(data);
    } finally { setRefreshing(false); }
  };

  // Which articles are mid-action, so each card can show its own spinner
  // instead of the whole list freezing.
  const [busy, setBusy] = useState({});
  const mark = (url, state) => setBusy((b) => ({ ...b, [url]: state }));

  const reloadItems = async () => {
    const { data } = await api.get("/feed");
    setItems(data);
  };

  const generateItem = async (it) => {
    mark(it.url, "generating");
    try {
      await api.post("/feed-items/generate", { source_id: it.source_id, url: it.url });
      toast.success("Post generated — the image is rendering");
      await reloadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate this post");
      await reloadItems();
    } finally { mark(it.url, null); }
  };

  const queueItem = async (it) => {
    mark(it.url, "queueing");
    try {
      await api.post("/feed-items/queue", { source_id: it.source_id, url: it.url });
      toast.success("Sent to the approval queue");
      await reloadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue this post");
    } finally { mark(it.url, null); }
  };

  const resetItem = async (it) => {
    mark(it.url, "resetting");
    try {
      await api.post("/feed-items/reset", { source_id: it.source_id, url: it.url });
      await reloadItems();
    } finally { mark(it.url, null); }
  };

  // An image that is still rendering resolves in the background, so the list
  // keeps itself current while any post is waiting on one.
  useEffect(() => {
    const pending = items.some(
      (i) => i.post?.image_status === "pending" || i.status === "generating");
    if (!pending) return undefined;
    const t = setInterval(() => { reloadItems().catch(() => {}); }, 6000);
    return () => clearInterval(t);
  }, [items]);   // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount = sources.filter((s) => s.active).length;

  return (
    <div className="space-y-8" data-testid="news-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Automation</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50">News &amp; Feeds</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">
            Each feed carries its own settings — brand, format, schedule and where the output
            goes. An active feed turns every new article into a post on its own.
            {activeCount > 0 && ` ${activeCount} feed${activeCount === 1 ? " is" : "s are"} running.`}
          </p>
        </div>
        <button onClick={() => openForm(null)}
          className="px-4 py-2.5 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2 shadow-[0_0_24px_rgba(99,102,241,0.35)]">
          <Plus className="w-4 h-4" /> Add feed
        </button>
      </div>

      <GenerationControls />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : sources.length === 0 ? (
        <div className="glass rounded-2xl py-20 flex flex-col items-center gap-3">
          <Rss className="w-8 h-8 text-slate-700" />
          <p className="text-slate-500 text-sm">No feeds yet. Add one to start.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map((f, i) => (
            <motion.div key={f.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
              className="glass rounded-2xl p-4" data-testid={`feed-${f.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-100">{f.name}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      f.active ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700/60 text-slate-400"}`}>
                      {f.active ? "Active" : "Paused"}
                    </span>
                    {f.content_type && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">
                        {f.content_type}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 truncate mt-0.5">{f.url}</p>
                  <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500 mt-1.5">
                    <span title={f.last_run ? `Last checked ${new Date(f.last_run).toLocaleString()}` : ""}>
                      {every(f.check_interval)}{f.last_run ? ` · checked ${ago(f.last_run)}` : ""}{nextIn(f) ? ` · ${nextIn(f)}` : ""}
                    </span>
                    {f.make_social && <span className="text-indigo-300">Social · {f.social_fmt}</span>}
                    {f.make_article && <span className="text-sky-300">Blog · {f.wp_status}</span>}
                    {f.generated > 0 && <span className="text-emerald-400">{f.generated} generated</span>}
                    {f.failed > 0 && (
                      <span className="flex items-center gap-1 text-amber-400">
                        <AlertTriangle className="w-3 h-3" /> {f.failed} failed
                      </span>
                    )}
                    {f.last_error && <span className="text-rose-400 truncate max-w-[240px]">{f.last_error}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => runNow(f)} title="Run now"
                    className="p-2 rounded-lg bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25">
                    <Play className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleActive(f)} title={f.active ? "Pause" : "Activate"}
                    className={`p-2 rounded-lg ${f.active ? "text-emerald-400" : "text-slate-600"} hover:bg-slate-800`}>
                    <Power className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => openForm(f)} title="Edit"
                    className="p-2 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(f.id)} title="Delete"
                    className="p-2 rounded-lg text-slate-600 hover:text-rose-400 hover:bg-slate-800">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Recent items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-slate-100">Recent articles</h3>
          <div className="flex items-center gap-3">
            {itemCounts.failed > 0 && (
              <button onClick={dismissFailed}
                className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200">
                <AlertTriangle className="w-3.5 h-3.5" /> Dismiss {itemCounts.failed} failed
              </button>
            )}
            <button onClick={refresh} disabled={refreshing}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200 disabled:opacity-60">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {[["all", "All"], ["ready", "Generated"], ["todo", "Not generated"], ["failed", "Failed"], ["skipped", "Skipped"]]
            .filter(([k]) => k !== "skipped" || itemCounts.skipped > 0).map(([k, l]) => (
            <button key={k} onClick={() => setItemFilter(k)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${itemFilter === k
                ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-100" : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>
              {l} <span className="opacity-60">{itemCounts[k]}</span>
            </button>
          ))}
        </div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl py-12 flex flex-col items-center gap-2">
            <Newspaper className="w-6 h-6 text-slate-700" />
            <p className="text-slate-500 text-sm">Nothing fetched yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {shownItems.slice(0, 60).map((it) => (
              <FeedItemCard
                key={it.url} item={it} busy={busy[it.url]}
                onGenerate={() => generateItem(it)}
                onQueue={() => queueItem(it)}
                onReset={() => resetItem(it)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Feed setup */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{form?.id ? "Edit feed" : "Add feed"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Feed name</label>
                  <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="US Magazine" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Content type</label>
                  <input value={form.content_type} onChange={(e) => set("content_type", e.target.value)} placeholder="Magazine, Technology…" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Feed URL</label>
                <input data-testid="feed-url" value={form.url} onChange={(e) => set("url", e.target.value)}
                  placeholder="https://www.usmagazine.com/feed" className={inputCls} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Check for new articles</label>
                  <select value={form.check_interval} onChange={(e) => set("check_interval", Number(e.target.value))} className={inputCls}>
                    {!INTERVALS.some(([v]) => v === form.check_interval) && <option value={form.check_interval}>{every(form.check_interval)}</option>}
                    {INTERVALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <p className={hintCls}>How often the feed is read for new articles while it is active. "Run now" checks immediately.</p>
                </div>
                <div>
                  <label className={labelCls}>Category filter (optional)</label>
                  <input value={form.category_filter} onChange={(e) => set("category_filter", e.target.value)}
                    placeholder="e.g. Movies" className={inputCls} />
                  <p className={hintCls}>Only process articles mentioning this.</p>
                </div>
              </div>

              {/* Social */}
              <div className="border-t border-slate-800 pt-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.make_social} onChange={(e) => set("make_social", e.target.checked)}
                    className="accent-indigo-500 w-4 h-4" />
                  <span className="text-sm font-semibold text-slate-200">Generate a social post per article</span>
                </label>
                {form.make_social && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Publish to</label>
                      {accounts.length === 0 ? (
                        <>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(PLATFORM_META).map(([key, meta]) => {
                              const on = (form.platforms || []).includes(key);
                              return (
                                <button key={key} type="button"
                                  onClick={() => set("platforms", on
                                    ? form.platforms.filter((x) => x !== key)
                                    : [...(form.platforms || []), key])}
                                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                                    on ? "text-white" : "border-slate-800 text-slate-400 hover:border-slate-700"}`}
                                  style={on ? { background: `${meta.color}22`, borderColor: meta.color } : {}}>
                                  {meta.label}
                                </button>
                              );
                            })}
                          </div>
                          <p className={hintCls}>
                            No accounts connected yet. Connect them under Accounts to send this
                            feed to a specific page.
                          </p>
                        </>
                      ) : (
                        // With several pages per platform, the account IS the choice -
                        // "Instagram" alone does not say which of them publishes.
                        <div className="space-y-3">
                          {Object.keys(PLATFORM_META)
                            .filter((key) => accounts.some((a) => a.platform === key) || (form.platforms || []).includes(key))
                            .map((key) => {
                              const meta = PLATFORM_META[key];
                              const mine = accounts.filter((a) => a.platform === key);
                              const chosen = mine.filter((a) => (form.account_ids || []).includes(a.id));
                              const all = chosen.length === 0;
                              return (
                                <div key={key} className="rounded-xl border border-slate-800 p-3">
                                  <div className="flex items-center justify-between gap-2 mb-2">
                                    <span className="flex items-center gap-2 text-xs font-semibold text-slate-200">
                                      <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                                      {meta.label}
                                      <span className="text-slate-600 font-normal">
                                        {mine.length} {mine.length === 1 ? "account" : "accounts"}
                                      </span>
                                    </span>
                                    <button type="button" onClick={() => toggleWholePlatform(key, mine)}
                                      className="text-[11px] font-semibold text-slate-500 hover:text-slate-300">
                                      {(form.platforms || []).includes(key) ? "Turn off" : "Turn on"}
                                    </button>
                                  </div>
                                  {(form.platforms || []).includes(key) && mine.length === 0 && (
                                    <p className="text-[11px] text-amber-300 flex items-start gap-1.5">
                                      <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                                      No {meta.label} account is connected, so posts cannot go here. Turn it off, or connect one under Accounts.
                                    </p>
                                  )}
                                  {(form.platforms || []).includes(key) && mine.length > 0 && (
                                    <>
                                      <div className="flex flex-wrap gap-2">
                                        {mine.map((a) => {
                                          const on = (form.account_ids || []).includes(a.id);
                                          return (
                                            <button key={a.id} type="button"
                                              data-testid={`feed-account-${a.id}`}
                                              onClick={() => toggleAccount(key, a.id)}
                                              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
                                                on ? "text-white" : "border-slate-800 text-slate-400 hover:border-slate-700"}`}
                                              style={on ? { background: `${meta.color}22`, borderColor: meta.color } : {}}>
                                              {a.display_name || a.name || a.platform}{a.account_kind === "page" && <span className="ml-1 text-[9px] px-1 rounded bg-sky-500/15 text-sky-300">Page</span>}
                                              {a.status === "reconnect_needed" && (
                                                <span className="text-amber-400" title="Needs reconnecting">!</span>
                                              )}
                                              {a.connection_type === "demo" && (
                                                <span className="text-slate-500 font-normal">demo</span>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <p className={hintCls}>
                                        {all
                                          ? `Nothing picked, so every ${meta.label} account above gets the post.`
                                          : `${chosen.length} of ${mine.length} selected.`}
                                      </p>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}
                      {!(form.platforms || []).length && (
                        <p className="text-[11px] text-amber-400 mt-1.5">
                          Pick at least one channel or these posts have nowhere to go.
                        </p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className={labelCls}>When to generate</label>
                      <select value={form.generate_mode || "auto"} onChange={(e) => set("generate_mode", e.target.value)}
                        className={inputCls}>
                        <option value="auto">Automatically, as articles arrive</option>
                        <option value="manual">Only when I press Generate</option>
                      </select>
                      <p className={hintCls}>
                        Manual collects the articles and leaves each post to you, so you can
                        review the image and caption before anything moves on.
                      </p>
                    </div>
                    <div>
                      <label className={labelCls}>Brand</label>
                      <select value={form.brand_id || ""} onChange={(e) => set("brand_id", e.target.value || null)} className={inputCls}>
                        <option value="">House style</option>
                        {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                      <p className={hintCls}>Palette, tone and logo come from here.</p>
                    </div>
                    <div>
                      <label className={labelCls}>Format</label>
                      <select value={form.social_fmt} onChange={(e) => set("social_fmt", e.target.value)} className={inputCls}>
                        <option value="viral">Viral news card</option>
                        <option value="static">Static</option>
                        <option value="carousel">Carousel</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Image ratio</label>
                      <select value={form.social_ratio} onChange={(e) => set("social_ratio", e.target.value)} className={inputCls}>
                        {ratios.map((r) => <option key={r.id} value={r.id}>{r.id} — {r.renders_as}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Image model</label>
                      <select value={form.image_model} onChange={(e) => set("image_model", e.target.value)} className={inputCls}>
                        <option value="">Use the default</option>
                        {models.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>When a post is ready</label>
                      <select value={form.approval_mode || (form.auto_submit ? "manual" : "draft")}
                        onChange={(e) => set("approval_mode", e.target.value)} className={inputCls}>
                        <option value="draft">Keep it here for me to look at</option>
                        <option value="manual">Send it to the approval queue</option>
                        <option value="auto" disabled={me?.plan?.auto_approve === false}>
                          Publish it if it passes the hard checks{me?.plan?.auto_approve === false ? " (Pro)" : ""}
                        </option>
                        <option value="quality" disabled={me?.plan?.auto_approve === false}>
                          Publish it if it also clears the quality score{me?.plan?.auto_approve === false ? " (Pro)" : ""}
                        </option>
                      </select>
                      <p className={hintCls}>
                        Auto modes never publish a post with a missing image, a broken caption or a repeated
                        story - those always wait for you, with the reason attached.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Blog */}
              <div className="border-t border-slate-800 pt-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.make_article} onChange={(e) => set("make_article", e.target.checked)}
                    className="accent-indigo-500 w-4 h-4" />
                  <span className="text-sm font-semibold text-slate-200">Write a blog article per article</span>
                </label>
                {form.make_article && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                    <div className="sm:col-span-2">
                      <label className={labelCls}>WordPress site</label>
                      <select value={form.wp_site_id || ""}
                        onChange={(e) => { set("wp_site_id", e.target.value || null); loadTaxonomy(e.target.value); }}
                        className={inputCls}>
                        <option value="">Keep as a draft here only</option>
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <p className={hintCls}>Configure sites under Blog.</p>
                    </div>
                    <div>
                      <label className={labelCls}>WordPress category</label>
                      <select value={form.wp_category || ""} onChange={(e) => set("wp_category", Number(e.target.value) || null)} className={inputCls}>
                        <option value="">None</option>
                        {taxonomy.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>WordPress author</label>
                      <select value={form.wp_author || ""} onChange={(e) => set("wp_author", Number(e.target.value) || null)} className={inputCls}>
                        <option value="">Default</option>
                        {taxonomy.authors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Publish as</label>
                      <select value={form.wp_status} onChange={(e) => set("wp_status", e.target.value)} className={inputCls}>
                        <option value="draft">Draft</option>
                        <option value="publish">Published</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Article length (words)</label>
                      <input type="number" min={300} max={2500} step={100} value={form.article_words}
                        onChange={(e) => set("article_words", Number(e.target.value))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Images inside the article</label>
                      <select value={form.inline_images_source || "ai_generated"}
                        onChange={(e) => set("inline_images_source", e.target.value)} className={inputCls}>
                        <option value="ai_generated">AI generated</option>
                        <option value="feed_source">From the feed (AI when it runs out)</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    {form.inline_images_source !== "none" && (
                      <div>
                        <label className={labelCls}>How many</label>
                        <input type="number" min={0} max={20} value={form.inline_images_count ?? 2}
                          onChange={(e) => set("inline_images_count", Number(e.target.value))} className={inputCls} />
                        <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                          <input type="checkbox" checked={!!form.auto_detect_image_count}
                            onChange={(e) => set("auto_detect_image_count", e.target.checked)} className="accent-indigo-500" />
                          <span className="text-[11px] text-slate-400">List titles set the count ("10 Best…" → 10, max 20)</span>
                        </label>
                      </div>
                    )}
                    <label className="sm:col-span-2 flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.social_embeds !== false}
                        onChange={(e) => set("social_embeds", e.target.checked)} className="accent-indigo-500 w-4 h-4" />
                      <span className="text-xs text-slate-300">Re-embed X and Instagram posts from the source article</span>
                    </label>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Featured image source</label>
                      <select value={form.featured_image_source || "ai_generated"}
                        onChange={(e) => set("featured_image_source", e.target.value)} className={inputCls}>
                        {FEATURED_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <p className={hintCls}>
                        {{ feed_source: "The article's own image; generated when it has none.",
                           pexels: "Top Pexels match for the focus keyphrase; generated when none is found.",
                           none: "Articles go out without one." }[form.featured_image_source]
                          || "A photorealistic editorial photo - no text, no logo, no brand styling."}
                      </p>
                    </div>
                    {form.featured_image_source !== "none" && (
                      <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Aspect ratio</label>
                          <select value={form.featured_ratio || "16:9"} onChange={(e) => set("featured_ratio", e.target.value)} className={inputCls}>
                            <option value="16:9">16:9 (Landscape)</option>
                            <option value="1:1">1:1 (Square)</option>
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Image model</label>
                          <select value={form.featured_model || ""} onChange={(e) => set("featured_model", e.target.value)} className={inputCls}>
                            <option value="">Same as social</option>
                            {models.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
                          </select>
                        </div>
                      </div>
                    )}

                    {form.make_social && form.wp_site_id && (
                      <div className="sm:col-span-2 rounded-xl border border-violet-500/20 bg-violet-500/[0.05] p-4 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={form.link_to_article !== false}
                            onChange={(e) => set("link_to_article", e.target.checked)} className="accent-violet-500 w-4 h-4" />
                          <span className="text-sm font-semibold text-slate-200">Send traffic to the article</span>
                        </label>
                        {form.wp_status !== "publish" ? (
                          <p className={hintCls}>Links are only added when articles are published live - a draft's link is a private preview.</p>
                        ) : form.link_to_article !== false && (
                          <>
                            <div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400">Posts that carry the link</span>
                                <span className="text-white font-semibold">{form.link_ratio}%</span>
                              </div>
                              <input type="range" min={0} max={100} step={10} value={form.link_ratio}
                                onChange={(e) => set("link_ratio", Number(e.target.value))} className="w-full accent-violet-500 mt-1.5" />
                              <p className={hintCls}>
                                LinkedIn and Facebook show link posts to fewer people, so not every post should carry one.
                                The rest go out native, for reach. 40–60% is a good balance.
                              </p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <label className={labelCls}>Lead-in (LinkedIn, Facebook)</label>
                                <input value={form.link_cta} onChange={(e) => set("link_cta", e.target.value)} className={inputCls} />
                                <p className={hintCls}>Shown as "{form.link_cta || "Read the full story"}: https://…"</p>
                              </div>
                              <div>
                                <label className={labelCls}>Instagram line</label>
                                <input value={form.instagram_link_line} onChange={(e) => set("instagram_link_line", e.target.value)} className={inputCls} />
                                <p className={hintCls}>Instagram caption links aren't clickable, so it points to the bio instead.</p>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.link_utm !== false} onChange={(e) => set("link_utm", e.target.checked)}
                                  className="accent-violet-500 w-4 h-4" />
                                <span className="text-xs text-slate-300">Tag links for Google Analytics (UTM)</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={Boolean(form.link_track_clicks)} onChange={(e) => set("link_track_clicks", e.target.checked)}
                                  className="accent-violet-500 w-4 h-4" />
                                <span className="text-xs text-slate-300">Count clicks here too (short link)</span>
                              </label>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-800 pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)}
                    className="accent-emerald-500 w-4 h-4" />
                  <span className="text-sm font-semibold text-slate-200">Active</span>
                </label>
                <p className={hintCls}>
                  While active, every new article from this feed is processed automatically on the
                  interval above. Leave it off and use Run now to test first.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg text-sm text-slate-400">Cancel</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {form?.id ? "Update feed" : "Add feed"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
